import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { subscribeUserWithFullData } from '../../utils/mailchimp';
import { MAILCHIMP_TAGS } from '../../utils/mailchimp-tags';

export default function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    signUp, 
    signInWithGoogle, 
    user, 
    profile, 
    ready,
    isLoading,
    hasActiveSubscription
  } = useAuth();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const mounted = useRef(true);

  // Get redirect URL from query params
  const requestedRedirect = new URLSearchParams(location.search).get('redirect');

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  // Redirect logic - matches AuthRedirect.js flow
  useEffect(() => {
    // Don't redirect until auth is ready
    if (!ready || isLoading) return;

    // Don't redirect if not authenticated
    if (user === false) return;

    // Don't redirect if still loading user data
    if (user === null) return;

    // Now we know we have an authenticated user
    if (!profile?.onboarding_completed) {
      navigate('/onboarding', { replace: true });
    } else if (!hasActiveSubscription) {
      // If there's a requested redirect to payment, don't redirect again
      if (requestedRedirect?.includes('/payment')) return;
      navigate('/payment', { replace: true });
    } else {
      navigate(requestedRedirect || '/dashboard', { replace: true });
    }
  }, [user, profile, ready, isLoading, hasActiveSubscription, navigate, requestedRedirect]);

  const validateForm = () => {
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (name.length < 2) {
      setError('Name must be at least 2 characters');
      return false;
    }
    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validateForm()) return;
    
    setFormLoading(true);
    try {
      const { data, error: signUpError } = await signUp(email, password, name);
      
      if (signUpError) {
        // Handle specific signup errors
        const errorMessage = signUpError.message || signUpError.toString();
        
        if (errorMessage.includes('already registered')) {
          setError('An account with this email already exists. Please sign in instead.');
          return;
        }
        if (errorMessage.includes('Password should be at least')) {
          setError('Password must be at least 6 characters long.');
          return;
        }
        if (errorMessage.includes('Invalid email')) {
          setError('Please enter a valid email address.');
          return;
        }
        if (errorMessage.includes('Rate limit') || errorMessage.includes('Too many requests')) {
          setError('Too many signup attempts. Please wait a few minutes before trying again. If you continue to have issues, contact our support team via the chat widget (bottom right) or email support@openorg.fyi');
          return;
        }
        
        // For any other error, show a user-friendly message
        setError(`${errorMessage || 'Unable to create account.'} Please try again or contact our support team via the chat widget (bottom right) or email support@openorg.fyi if the problem persists.`);
        return;
      }

      if (data?.confirmationEmailSent) {
        setSuccess('🎉 Welcome to Open Org! We\'ve sent a confirmation email to your inbox. Please check your email (and spam folder) to verify your account and get started.');
        return;
      }

      if (!data?.user) {
        setError('Unable to create account. Please try again or contact our support team via the chat widget (bottom right) or email support@openorg.fyi if the problem persists.');
        return;
      }

      // Subscribe user to Mailchimp after successful signup (non-blocking)
      const [firstName, ...lastNameParts] = name.trim().split(' ');
      const lastName = lastNameParts.join(' ');
      
      // Fire-and-forget with proper error handling
      (async () => {
        try {
          await subscribeUserWithFullData({
            email: email,
            firstName: firstName || '',
            lastName: lastName || '',
            supabaseId: data.user.id,
            tags: MAILCHIMP_TAGS.SIGNUP_TAGS
          });
          // console.log('Successfully subscribed user to Mailchimp');
        } catch (mailchimpError) {
          console.error('Failed to subscribe to Mailchimp:', mailchimpError);
          // Could also send to error tracking service here
        }
      })();

      setSuccess('🎉 Welcome to Open Org! We\'ve sent a confirmation email to your inbox. Please check your email (and spam folder) to verify your account and get started.');
      // Navigation handled by effect
    } catch (err) {
      // Handle unexpected errors
      if (err.message && err.message.includes('already registered')) {
        setError('An account with this email already exists. Please sign in instead.');
        return;
      }
      
      setError(`${err.message || 'An unexpected error occurred.'} If you continue to have issues, please contact our support team via the chat widget (bottom right) or email support@openorg.fyi`);
    } finally {
      setFormLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setSuccess('');
    setGoogleLoading(true);
    
    try {
      const { success, error } = await signInWithGoogle();

      if (!success) {
        setError(`${error || 'Google sign in failed.'} Please try again or contact our support team via the chat widget (bottom right) or email support@openorg.fyi if the problem persists.`);
        return;
      }

      setSuccess('Successfully signed in with Google! Redirecting...');
      // For Google sign in, the redirect is handled by the OAuth flow and AuthRedirect.js
    } catch (err) {
      setError(`${err.message || 'Google sign in failed.'} Please try again or contact our support team via the chat widget (bottom right) or email support@openorg.fyi if the problem persists.`);
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Become An Open Org Member</h1>
          <p className="text-gray-400 mb-1">Create your account to access our curated playbooks and exclusive marketplace</p>
          <p className="text-sm text-primary mt-2">£120/year annual membership</p>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          {/* Success Message */}
          {success && (
            <div className="p-3 mb-4 bg-green-500/20 border border-green-500 rounded">
              <p className="text-green-300 text-center">{success}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 mb-4 bg-red-500/20 border border-red-500 rounded">
              <p className="text-red-300 text-center">{error}</p>
            </div>
          )}

          {/* Google Sign In Button */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={formLoading || googleLoading}
            className="w-full mb-4 py-2 px-4 bg-white text-gray-900 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {googleLoading ? 'Connecting...' : 'Continue with Google'}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-600" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-800 text-gray-400">Or sign up with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                disabled={formLoading || googleLoading}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={formLoading || googleLoading}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password (min. 6 characters)"
                disabled={formLoading || googleLoading}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                required
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                disabled={formLoading || googleLoading}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                required
              />
            </div>

            <button
              type="submit"
              disabled={formLoading || googleLoading}
              className="w-full py-2 px-4 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formLoading ? 'Creating Account...' : 'Sign Up'}
            </button>
          </form>
        </div>

        <p className="mt-4 text-center text-sm text-gray-400">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/signin')}
            disabled={formLoading || googleLoading}
            className="text-primary hover:text-primary/90 focus:outline-none focus:underline disabled:opacity-50"
          >
            Sign in
          </button>
        </p>
        
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            By creating an account, you'll be directed to complete your membership setup. 
            Cancel anytime during your subscription.
          </p>
        </div>
      </div>
    </div>
  );
} 